// SECURITY FIX: Frontend no longer generates HTML - only sends JSON data to backend
import type { Draft, InvoiceTranslations } from '../types';
import type { DesignSettings } from '../DesignPanel';
import DOMPurify from 'dompurify';

export interface PdfGenerationParams {
    draft: Draft;
    translations: InvoiceTranslations;
    designSettings: DesignSettings;
    fontFamily?: string;
}

// Helper to sanitize object recursively
const sanitizeData = (data: any): any => {
    if (typeof data === 'string') {
        return DOMPurify.sanitize(data);
    }
    if (Array.isArray(data)) {
        return data.map(item => sanitizeData(item));
    }
    if (data && typeof data === 'object') {
        const sanitized: any = {};
        for (const [key, value] of Object.entries(data)) {
            sanitized[key] = sanitizeData(value);
        }
        return sanitized;
    }
    return data;
};

/**
 * Generate PDF via backend API
 * SECURITY FIX: Frontend sends only JSON data, HTML is generated on backend from trusted templates
 * This prevents RCE/SSRF/LFI vulnerabilities
 */
export async function generateAndDownloadPdf(
    params: PdfGenerationParams,
    backendUrl: string = ''
): Promise<void> {
    try {
        // Sanitize all input data using DOMPurify
        const sanitizedParams = sanitizeData(params);
        
        // Бэкенд принимает любые данные, даже пустые - валидация не нужна

        // Generate filename based on invoice number (for download only)
        const sanitizeFilename = (filename: string) => {
            return filename
                .toLowerCase()
                .replace(/[^a-z0-9\-_]/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
        };

        const filename = `invoice-${sanitizeFilename(sanitizedParams.draft.invoiceNo || '')}.pdf`;

        // Prepare data for sending - бэкенд принимает любые данные, даже undefined
        const requestData = {
            draft: sanitizedParams.draft || {},
            translations: sanitizedParams.translations || {},
            fontFamily: sanitizedParams.fontFamily || 'Inter, Arial, sans-serif',
            designSettings: sanitizedParams.designSettings || {},
        };
        
        // Send only JSON data to backend - NO HTML
        // Backend will generate HTML from trusted template
        const response = await fetch(`${backendUrl}/generate-pdf`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData)
        });

        if (!response.ok) {
            let message = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                if (errorData?.details) {
                    message = errorData.details;
                } else if (errorData?.error) {
                    message = errorData.error;
                }
            } catch {
                // ignore JSON parse errors, keep default message
            }
            throw new Error(message);
        }

        // Create blob from response
        const blob = await response.blob();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    }
}
